/* @flow */
import classNames from 'classnames';
import React, {Component} from 'react';
import {findDOMNode} from 'react-dom';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Button, ButtonPrimary} from '@homeaway/react-buttons';
import {HA_SERP_THREE_PACK} from '../../abacus/experiments';
import ForFamiliesFilter from '../../react-segmentation/components/ForFamiliesFilter/ForFamiliesFilter';
import BedroomsFilter from '../BedroomsFilter/BedroomsFilter';
import searchParamsActions from '../../actions/searchParams';
import {cannotFilter} from '../../utils/searchParamsUtils';
import BathroomsFilter from '../BathroomsFilter/BathroomsFilter';
import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import keyboardConstants from '../../constants/keyboardConstants';
import {renderFilterGroups} from '../MoreFilters/MoreFilters';
import seeResultsMessage from '../SeeResultsMessage/SeeResultsMessage';
import PriceSlider from '../PriceSlider/PriceSlider';
import {instantBookFilter, newArrayFromFilters} from '../../utils/filters';
import type {BoundsType} from '../PriceSlider/PriceSlider';
import type {ChildrenArray} from 'react';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {State as ReduxState} from '../../reducers';
import type {IntlShape} from 'react-intl';
import {triggerEdapEventForCheckbox} from '../../utils/filtersEdapUtils';
import {media} from '../../utils/breakpoints';

import './MoreFiltersPanel.less';

type StateProps = {
    canFit3Columns: boolean,
    canFit4Columns: boolean,
    currency: string,
    locale: string,
    filter: ?(string[]),
    hasDates: boolean,
    isThreePackTest: boolean,
    isThreePackTestV2: boolean,
    inputParamsAreChanged: boolean,
    inputMaxBedrooms: ?number,
    inputMinBedrooms: ?number,
    inputMaxPrice: ?number,
    inputMinPrice: ?number,
    maxBathrooms: ?number,
    minBathrooms: ?number,
    selectedFilters: string[],
    topBannersHeight: number
};

type InjectIntlProps = {
    intl: IntlShape
};

type Props = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number,
    isVisible?: boolean,
    onApplyFilters: (void) => void,
    onCancel: () => void,
    onInputParamsRangeFilterChange: (SearchParamsType) => void,
    updateInputParams: (SearchParamsType) => void,
    updateFilterCheck: (HTMLInputElement) => void
} & StateProps &
    InjectIntlProps;

export class MoreFiltersPanel extends Component<Props> {
    moreFiltersContentRef: ?HTMLDivElement;

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps: Props) {
        const {topBannersHeight, isVisible} = this.props;
        const node = findDOMNode(this);
        const {moreFiltersContentRef} = this;

        /* adjust panel's position */
        if (
            node instanceof Element &&
            node.parentNode &&
            node.parentNode instanceof HTMLElement &&
            (prevProps.topBannersHeight !== topBannersHeight ||
                parseInt(
                    window.getComputedStyle(node.parentNode).marginTop,
                    10
                ) !== topBannersHeight)
        ) {
            node.parentNode.style.setProperty(
                'margin-top',
                `${topBannersHeight}px`
            );
        }

        /* focus content for keyboard input */
        if (!prevProps.isVisible && isVisible && moreFiltersContentRef) {
            /* wait till next cycle to focus to make sure dropdown is open before focusing since
            we cannot focus an element with 0 height */
            setTimeout(() => {
                moreFiltersContentRef.focus();
            }, 0);
        }

        /* If we are hidden, scroll the user back to the top of the panel */
        if (!isVisible && moreFiltersContentRef) {
            moreFiltersContentRef.scrollTop = 0;
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown = (e: SyntheticKeyboardEvent<*>) => {
        const {isVisible} = this.props;
        if (isVisible && e.which === keyboardConstants.ESC) {
            this.props.onApplyFilters();
        }
    };

    handleMoreFilterCheck = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event);

        this.props.updateFilterCheck(event.currentTarget);
    };

    getColumns = (children: ChildrenArray<*>, columns: number = 2) => {
        let arr = [];

        for (let i = 1; i <= children.length; i++) {
            const modulus = i % columns;
            arr[modulus] = arr[modulus] || [];
            arr[modulus].push(children[i - 1]);
        }

        /* istanbul ignore next */
        if (arr[columns - 1]) {
            arr[1].unshift(
                <ForFamiliesFilter
                    key="families-filter"
                    onSwitchChange={this.handleMoreFilterCheck}
                    className={classNames(
                        'MoreFiltersPanel__filter-section',
                        'MoreFiltersPanel__filter-section--bordered'
                    )}
                />
            );
        }

        return arr;
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {updateInputParams} = this.props;
        const values = {
            minPrice: bounds[0] || 0,
            maxPrice: bounds[1]
        };
        updateInputParams(values);
    };

    setMoreFiltersContentRef = (component: ?HTMLDivElement) => {
        this.moreFiltersContentRef = component;
    };

    renderPrimaryFilters = (className: string = '') => {
        const {
            currency,
            locale,
            hasDates,
            filter,
            inputMaxPrice,
            inputMinPrice,
            onInputParamsRangeFilterChange,
            inputMinBedrooms,
            inputMaxBedrooms,
            minBathrooms,
            maxBathrooms
        } = this.props;
        const isInstantConfirmationChecked = !!instantBookFilter(filter);
        return (
            <div key="primaryFiltersMoreFiltersPanel" className={className}>
                <PriceSlider
                    currency={currency}
                    hasDates={hasDates}
                    onPriceChange={this.handlePriceChange}
                    minPrice={inputMinPrice}
                    maxPrice={inputMaxPrice}
                    className="MoreFiltersPanel__item"
                    locale={locale}
                />
                <BedroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBedrooms={inputMinBedrooms || 0}
                    maxBedrooms={inputMaxBedrooms}
                    className="MoreFiltersPanel__item"
                />
                <BathroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBathrooms={minBathrooms || 0}
                    maxBathrooms={maxBathrooms}
                    className="MoreFiltersPanel__item"
                />
                <InstantConfirmationFilter
                    id="moreFiltersConfirmation__switch"
                    checked={isInstantConfirmationChecked}
                    handleChecked={this.handleMoreFilterCheck}
                    className="MoreFiltersPanel__item"
                />
            </div>
        );
    };

    render() {
        const {
            handleMoreFilterCheck,
            props: {
                isThreePackTest,
                isThreePackTestV2,
                filterGroups,
                filter,
                minBathrooms,
                maxBathrooms,
                intl,
                resultsCount,
                onApplyFilters,
                onCancel,
                onInputParamsRangeFilterChange,
                selectedFilters,
                canFit3Columns,
                canFit4Columns
            }
        } = this;

        const submitDisabled = cannotFilter(resultsCount);

        const cancelLabel = intl.formatMessage({
            id: 'formModalCloseText',
            description: 'cancel button',
            defaultMessage: 'Cancel'
        });

        const inputFilters = newArrayFromFilters({filter});
        const appliedFilters = selectedFilters;

        const childrenToRender = [
            ...renderFilterGroups(appliedFilters, {
                filterGroups,
                className: 'MoreFiltersPanelGroup',
                handleChecked: handleMoreFilterCheck,
                checked: inputFilters
            })
        ];

        const columns = this.getColumns(
            childrenToRender,
            isThreePackTestV2 && canFit4Columns ? 3 : 2
        );

        const bathroomsFilter = (
            <div
                key="bathrooms-filter"
                className={classNames('MoreFiltersPanel__filter-section', {
                    'MoreFiltersPanel__filter-section--bordered': !isThreePackTest
                })}
            >
                <BathroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBathrooms={minBathrooms || 0}
                    maxBathrooms={maxBathrooms}
                />
            </div>
        );

        /* add bathrooms filters to the top of the first column if we are not in
        variant 2 of the three pack test */
        if (!isThreePackTestV2 && columns[0]) {
            columns[0].unshift(bathroomsFilter);
        } else if (!isThreePackTestV2 && !columns[0]) {
            columns[0] = [bathroomsFilter];
        }

        /* If we are in variant 2 of the test (showing the primary filters)
        but we cannot fit 3 columns, we need to add the primary filters to the filter groups
        for the 2 columns */
        if (isThreePackTestV2 && !canFit3Columns) {
            columns[0].unshift(this.renderPrimaryFilters());
        }

        return (
            <div className="MoreFiltersPanel" data-wdio="MoreFiltersPanel">
                <div
                    tabIndex="0"
                    className={classNames('MoreFiltersPanel__content', {
                        HA_SERP_THREE_PACK: isThreePackTest
                    })}
                    ref={this.setMoreFiltersContentRef}
                >
                    {isThreePackTestV2 &&
                        canFit3Columns &&
                        this.renderPrimaryFilters(
                            'MoreFiltersPanel__column HA_SERP_THREE_PACK MoreFiltersPanel__column--left'
                        )}
                    {columns.map((component, index) => (
                        <div
                            className={classNames('MoreFiltersPanel__column', {
                                HA_SERP_THREE_PACK: isThreePackTest
                            })}
                            key={`MoreFiltersPanel-column-${index}`}
                        >
                            {component}
                        </div>
                    ))}
                </div>
                <div className={'MoreFiltersPanel__buttons--container'}>
                    <div
                        className={classNames('MoreFiltersPanel__buttons', {
                            HA_SERP_THREE_PACK: isThreePackTest
                        })}
                    >
                        <Button
                            id="moreFiltersPanel__btn--cancel"
                            label={cancelLabel}
                            onClick={onCancel}
                        />
                        <ButtonPrimary
                            id="moreFiltersPanel__btn--submit"
                            disabled={submitDisabled}
                            label={seeResultsMessage({
                                intl: this.props.intl,
                                resultsCount
                            })}
                            onClick={onApplyFilters}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = {
    updateInputParams: searchParamsActions.updateInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck
};

const mapStateToProps = ({
    abacus,
    searchParams: {
        inputParams: {
            filter,
            minBedrooms,
            maxBedrooms,
            minPrice,
            maxPrice,
            minBathrooms,
            maxBathrooms,
            arrival,
            departure
        },
        inputParamsAreChanged,
        selectedFilters
    },
    system: {currency, locale},
    topBanners: {height: topBannersHeight},
    viewport: {breakpoint}
}: ReduxState): StateProps => ({
    currency,
    locale,
    hasDates: !!(arrival && departure),
    isThreePackTest: !!(abacus[HA_SERP_THREE_PACK] || {}).value,
    isThreePackTestV2: (abacus[HA_SERP_THREE_PACK] || {}).value === 2,
    canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
    canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
    inputMinBedrooms: minBedrooms,
    inputMaxBedrooms: maxBedrooms,
    inputMinPrice: minPrice,
    inputMaxPrice: maxPrice,
    filter,
    minBathrooms,
    maxBathrooms,
    inputParamsAreChanged,
    selectedFilters,
    topBannersHeight
});

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(MoreFiltersPanel);
